import React, { Fragment, useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";

import HeaderV2 from "../../components/headerV2/HeaderV2";
import FooterV2 from "../../components/footerV2/FooterV2";
import AboutHero from "../../components/aboutHero/AboutHero";
import useScrollReveal from "../../hooks/useScrollReveal";

import metadataJSON from "../../data/metadata.json";
import schemadataJSON from "../../data/schemadata.json";
// AboutHero + .about-head/.about-prose/.about-chips styles live in aboutV2.css.
import "../AboutUsPage/aboutV2.css";
import "./corporateV2.css";

const EmailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path
      d="M4 4h4l2 5-2.5 1.5a11 11 0 0 0 6 6L15 14l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 2 6a2 2 0 0 1 2-2Z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CorporateeDonations = ({ path = "corporate-donations" }) => {
  const rootRef = useRef(null);
  useScrollReveal(rootRef);

  // Setting metadata and schemadata
  const [metadata, setMetadata] = useState({ title: "JJFIndia | Corporate Donations" });
  const [schemadata, setSchemadata] = useState({});

  useEffect(() => {
    let tempMetadata = metadataJSON[path];
    let tempSchemadata = schemadataJSON[path];

    if (tempMetadata) {
      setMetadata(tempMetadata);
    } else {
      tempMetadata = {
        title: "JJFIndia | Corporate Donations",
        description: "Partner with Jainam Jivika Foundation through CSR initiatives, employee engagement programmes and customised campaigns to empower communities across India.",
        keywords: "",
        robots: "index, follow",
        canonical: "https://jjfindia.com/corporate-donations",
        openGraph: {
          title: "JJFIndia | Corporate Donations",
          description: "Partner with Jainam Jivika Foundation through CSR initiatives, employee engagement programmes and customised campaigns to empower communities across India.",
          url: "https://jjfindia.com/corporate-donations",
        },
        twitter: {
          card: "summary_large_image",
          title: "JJFIndia | Corporate Donations",
          description: "Partner with Jainam Jivika Foundation through CSR initiatives, employee engagement programmes and customised campaigns to empower communities across India.",
        },
      };
      setMetadata(tempMetadata);
    }

    if (tempSchemadata) setSchemadata(tempSchemadata);
  }, [path]);

  return (
    <Fragment>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        <meta name="keywords" content={metadata.keywords} />
        <meta name="robots" content={metadata.robots} />
        <link rel="canonical" href={metadata.canonical} />
        <meta property="og:title" content={metadata.openGraph?.title} />
        <meta property="og:description" content={metadata.openGraph?.description} />
        <meta property="og:url" content={metadata.canonical} />
        <meta name="twitter:title" content={metadata.twitter?.title} />
        <meta name="twitter:description" content={metadata.twitter?.description} />
        <script type="application/ld+json">{JSON.stringify(schemadata, null, 2)}</script>
      </Helmet>

      <div className="jjfv2 corporate-live" ref={rootRef}>
        <HeaderV2 />

        <main className="page_content">
          {/* ---------- Page hero ---------- */}
          <AboutHero
            eyebrow="Partner With Us"
            title="Corporate Donations"
            crumbs={[{ label: "Home", to: "/" }, { label: "Corporate Donations" }]}
          />

          {/* ---------- Intro: partnership ---------- */}
          <section className="section">
            <div className="wrap">
              <div className="about-head">
                <span className="eyebrow">Corporate Donations</span>
                <h2>Make a Difference Through Partnership</h2>
              </div>
              <div className="about-prose about-prose--center reveal">
                <p>
                  Join hands with Jainam Jivika Foundation to create lasting change. Collaborate with
                  us through CSR initiatives, employee engagement programmes or customised campaigns
                  tailored to your company&apos;s values and goals. Together, we can empower communities
                  and create a brighter future.
                </p>
                <div className="about-chips">
                  <span className="about-chip">
                    <i className="fa-solid fa-hand-holding-heart"></i> CSR Initiatives
                  </span>
                  <span className="about-chip">
                    <i className="fa-solid fa-users"></i> Employee Engagement Programmes
                  </span>
                  <span className="about-chip">
                    <i className="fa-solid fa-bullseye"></i> Customised Campaigns
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* ---------- Contact details ---------- */}
          <section className="section section--mist">
            <div className="wrap">
              <div className="about-head">
                <span className="eyebrow">Get in touch</span>
                <h2>Our Contact Details</h2>
              </div>
              <div className="corp-contacts">
                <a className="corp-contact reveal" href="mailto:info@JJFIndia.com" rel="noopener noreferrer">
                  <span className="corp-contact__ic"><EmailIcon /></span>
                  <span className="corp-contact__k">Email Id</span>
                  <span className="corp-contact__v">info@jjfindia.com</span>
                </a>
                <a className="corp-contact reveal d1" href="tel:+919552895289" rel="noopener noreferrer">
                  <span className="corp-contact__ic"><PhoneIcon /></span>
                  <span className="corp-contact__k">Contact Number</span>
                  <span className="corp-contact__v">+91 95 5289 5289</span>
                </a>
              </div>
            </div>
          </section>
        </main>

        <FooterV2 />
      </div>
    </Fragment>
  );
};

export default CorporateeDonations;
